import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { API, api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BEAR_IMG = "/moments/a3.webp";
const PRICE = 97;

const COLORS = [
  { id: "brown", label: "The Brown Bear", img: "/moments/bear_brown.jpg" },
  { id: "black", label: "The Black Bear", img: "/moments/bear_black.jpg" },
];

const inputCls = "rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]";
const labelCls = "font-mono text-xs uppercase tracking-widest text-muted-foreground";

export function Bears() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ bears: 0, bear_goal: 1000 });
  const [step, setStep] = useState(null); // info -> form -> pay -> done
  const [bearName, setBearName] = useState("");
  const [color, setColor] = useState("brown");
  const [adopterName, setAdopterName] = useState("");
  const [cert, setCert] = useState(null);

  const loadStats = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const pct = Math.min(100, (stats.bears / stats.bear_goal) * 100);
  const todayLabel = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const openWizard = () => {
    setAdopterName((n) => n || (user ? user.name : ""));
    setStep("info");
  };

  const closeWizard = () => {
    setStep(null);
    setCert(null);
    setBearName("");
    setColor("brown");
  };

  return (
    <section id="bears" className="mx-auto max-w-7xl px-6 py-32 lg:px-12" data-testid="bears-section">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border border-[#33240E]/30" />
            <img src={BEAR_IMG} alt="Guests with their adopted kindness bears" className="relative aspect-[4/5] w-full object-cover saturate-[0.7]" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-7"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.4em] text-[#8A5A0F]">Chapter 02, Continued</p>
          <h2 className="mb-6 font-serif text-4xl font-black text-[#33240E] sm:text-5xl lg:text-6xl">
            Adopt one of<br /><span className="italic text-[#8A5A0F]">1,000 bears</span>
          </h2>
          <p className="mb-10 max-w-lg text-base font-light leading-relaxed text-muted-foreground">
            Each Kindness Bear adoption is $97 and fuels the movement all the way to the Ball —
            the activations, the hugs, and the night itself. Name your bear, choose its look,
            and its birthday becomes the day you chose kindness.
          </p>

          <div className="mb-10">
            <div className="mb-3 flex items-end justify-between">
              <span className="font-serif text-5xl font-black text-[#8A5A0F] lg:text-6xl" data-testid="bear-counter">{stats.bears}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">of {stats.bear_goal.toLocaleString()} adopted</span>
            </div>
            <div className="h-px w-full bg-white/10">
              <div className="h-px bg-[#33240E] transition-all duration-1000" style={{ width: `${pct}%` }} data-testid="bear-progress" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <button onClick={openWizard} data-testid="adopt-open-button"
              className="bg-[#33240E] px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]">
              Adopt a Bear — $97
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Certificate included · PayPal secure checkout</span>
          </div>
        </motion.div>
      </div>

      <Dialog open={!!step} onOpenChange={(open) => !open && closeWizard()}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-[#33240E]/25 bg-[#FFF9EA] text-[#33240E] sm:max-w-2xl" data-testid="adopt-dialog">

          {step === "info" && (
            <div data-testid="adopt-info-step">
              <DialogHeader>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A5A0F]">The Bear Adoption</p>
                <DialogTitle className="font-serif text-3xl font-bold">Here's how it works.</DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-4 pt-2 text-base font-light leading-relaxed text-muted-foreground">
                    <p>
                      When you adopt a Kindness Bear, you're not just bringing home a cuddly
                      companion — you're putting kindness in motion. Every $97 adoption fuels the
                      movement: 120 days of daily kindness activations, the hugs, and the road to
                      The Kindness Ball.
                    </p>
                    <p>
                      Your adoption comes with an official <span className="font-semibold text-[#33240E]">Certificate of Adoption</span> —
                      your bear's name chosen by you, its birthday the day you adopt, and its own
                      number in the 1,000.
                    </p>
                  </div>
                </DialogDescription>
              </DialogHeader>
              <div className="mt-8 border-t border-[#33240E]/15 pt-8 text-center">
                <p className="mb-6 font-serif text-2xl italic text-[#33240E]">Are you ready to adopt?</p>
                <button onClick={() => setStep("form")} data-testid="adopt-ready-button"
                  className="bg-[#33240E] px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517]">
                  Yes — I'm Ready
                </button>
              </div>
            </div>
          )}

          {step === "form" && (
            <div data-testid="adopt-form-step">
              <DialogHeader>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A5A0F]">Step 1 of 2</p>
                <DialogTitle className="font-serif text-3xl font-bold">Your bear's birth certificate</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Fill this in first — it's the fun part. Payment comes next.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 space-y-6">
                <div className="space-y-3">
                  <Label className={labelCls}>Choose your bear</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {COLORS.map((c) => (
                      <button key={c.id} type="button" onClick={() => setColor(c.id)} data-testid={`bear-color-${c.id}`}
                        className={`group border p-3 text-left transition-all duration-300 ${
                          color === c.id ? "border-[#8A5A0F] bg-[#FDF3D8]" : "border-[#33240E]/20 hover:border-[#8A5A0F]/60"
                        }`}>
                        <img src={c.img} alt={c.label} className="mb-3 aspect-square w-full object-cover" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#33240E]">{c.label}</span>
                        {color === c.id && <span className="ml-2 font-mono text-[10px] text-[#8A5A0F]">● chosen</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bear-name" className={labelCls}>Bear's name</Label>
                  <Input id="bear-name" data-testid="bear-name-input" value={bearName} onChange={(e) => setBearName(e.target.value)} maxLength={40}
                    className={inputCls} placeholder="e.g. Lovey, Honey, Hope..." />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adopter-name" className={labelCls}>Adopted by</Label>
                  <Input id="adopter-name" data-testid="adopter-name-input" value={adopterName} onChange={(e) => setAdopterName(e.target.value)} maxLength={120}
                    className={inputCls} placeholder="Your name on the certificate" />
                </div>

                <div className="space-y-1">
                  <Label className={labelCls}>Bear's birthday</Label>
                  <p className="font-serif text-xl italic text-[#33240E]" data-testid="bear-birthday">{todayLabel}</p>
                  <p className="text-xs font-light text-muted-foreground">The day you adopt becomes their birthday — forever.</p>
                </div>

                <button
                  onClick={() => {
                    if (!bearName.trim() || !adopterName.trim()) {
                      toast.error("Give your bear a name and add yours.");
                      return;
                    }
                    setStep("pay");
                  }}
                  data-testid="adopt-to-payment-button"
                  className="w-full bg-[#33240E] py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517]">
                  Continue to Payment — $97
                </button>
              </div>
            </div>
          )}

          {step === "pay" && (
            <div data-testid="adopt-pay-step">
              <DialogHeader>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A5A0F]">Step 2 of 2</p>
                <DialogTitle className="font-serif text-3xl font-bold">Make it official.</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Complete the $97 adoption and {bearName}'s certificate is yours instantly.
                </DialogDescription>
              </DialogHeader>

              <div className="my-6 border border-[#33240E]/15 bg-[#FDF3D8] p-5" data-testid="adopt-summary">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Certificate preview details</p>
                <p className="mt-2 font-serif text-xl font-bold text-[#33240E]">{bearName} — the {color === "black" ? "Black" : "Brown"} Bear</p>
                <p className="text-sm font-light text-muted-foreground">Adopted by {adopterName} · Born {todayLabel}</p>
              </div>

              <PayPalScriptProvider options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "black", shape: "rect", label: "pay", height: 48 }}
                  forceReRender={[bearName, color, adopterName]}
                  createOrder={async () => {
                    const { data } = await api.post("/paypal/orders", {
                      quantity: 1, bear_name: bearName, bear_color: color, adopter_name: adopterName,
                    });
                    return data.order_id;
                  }}
                  onApprove={async (d) => {
                    try {
                      const { data } = await api.post(`/paypal/orders/${d.orderID}/capture`);
                      if (data.status === "paid") {
                        const c = await api.post(`/paypal/orders/${d.orderID}/certificate`, {});
                        setCert(c.data);
                        setStep("done");
                        loadStats();
                      } else {
                        toast.error("Payment didn't complete — please try again.");
                      }
                    } catch {
                      toast.error("Couldn't confirm the payment. If you were charged, contact us.");
                    }
                  }}
                  onError={() => toast.error("PayPal hit a snag. Please try again.")}
                />
              </PayPalScriptProvider>

              <button onClick={() => setStep("form")} data-testid="adopt-back-button"
                className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-[#33240E]">
                ← Back to the certificate details
              </button>
            </div>
          )}

          {step === "done" && cert && (() => {
            const certUrl = `${API}/bears/certificate?bear_name=${encodeURIComponent(cert.bear_name)}&adopter=${encodeURIComponent(cert.adopter)}&n=${cert.adoption_no}&color=${cert.color}`;
            const shareText = `I just adopted ${cert.bear_name}, Kindness Bear No. ${cert.adoption_no} of 1,000, with Be Kind Community Inc! Adopt yours:`;
            const siteUrl = window.location.origin;
            return (
              <div data-testid="adopt-done-step">
                <DialogHeader>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A5A0F]">Kindness Bear No. {cert.adoption_no.toLocaleString()} of 1,000</p>
                  <DialogTitle className="font-serif text-3xl font-bold" data-testid="bear-certificate-title">Meet {cert.bear_name}.</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Download the certificate, share the joy — your adoption just moved the counter.
                  </DialogDescription>
                </DialogHeader>
                <img src={certUrl} alt={`Adoption certificate for ${cert.bear_name}`} className="w-full border border-[#33240E]/25" data-testid="bear-certificate-preview" />
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <a href={certUrl} download="bear-adoption-certificate.png" data-testid="bear-certificate-download"
                    className="bg-[#33240E] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517]">
                    Download Certificate
                  </a>
                  <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(siteUrl)}`}
                    target="_blank" rel="noreferrer" data-testid="bear-certificate-share-x"
                    className="border border-[#33240E]/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
                    Share on X
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(shareText)}`}
                    target="_blank" rel="noreferrer" data-testid="bear-certificate-share-facebook"
                    className="border border-[#33240E]/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
                    Share on Facebook
                  </a>
                  <button onClick={() => { navigator.clipboard.writeText(`${shareText} ${siteUrl}`); toast.success("Copied — paste it anywhere."); }}
                    data-testid="bear-certificate-copy"
                    className="border border-[#33240E]/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
                    Copy Link
                  </button>
                </div>
              </div>
            );
          })()}

        </DialogContent>
      </Dialog>
    </section>
  );
}
